import type { AbstractIntlMessages } from "next-intl";

/** A next-intl message tree: nested namespaces bottoming out in ICU strings. */
export type MessageTree = { [key: string]: string | MessageTree };

/**
 * Expand a tenant's flat dictionary into the nested shape next-intl expects, so
 * a Squidex key of `poi.fallbackTitle` lines up with the repo catalog's
 * `{ poi: { fallbackTitle } }`. Keys without a dot stay at the root.
 *
 * A key is dropped when a path segment collides with an already-set string
 * (e.g. both `poi` and `poi.fallbackTitle` authored): tenants write these by
 * hand in a CMS, and a bad key must not take the whole catalog down with it.
 */
export function expandDottedKeys(
  entries: Iterable<readonly [string, string]>
): MessageTree {
  const root: MessageTree = {};

  for (const [key, value] of entries) {
    const path = key.split(".").filter(Boolean);
    if (path.length === 0) continue;

    let node = root;
    let usable = true;
    for (const segment of path.slice(0, -1)) {
      const child = node[segment];
      if (child === undefined) {
        node = node[segment] = {};
      } else if (typeof child === "object") {
        node = child;
      } else {
        usable = false;
        break;
      }
    }

    if (usable) node[path[path.length - 1]] = value;
  }

  return root;
}

/**
 * Where a tenant's own dictionary is mounted whole, alongside the merge.
 *
 * The merge below can't place every tenant key at the root, and the keys it has
 * to drop are exactly the ones the app looks up by name at runtime — a POI field
 * called `contact`, an order option on `share`. Mounting the dictionary here as
 * well means `useProjectText` can always reach them.
 *
 * Named for the legacy `t('project:key')` prefix this replaces.
 */
export const PROJECT_NAMESPACE = "project";

/**
 * Deep-merge tenant `overrides` onto the repo `base`. Overrides win for strings
 * and namespaces merge recursively, so a tenant only has to author the keys it
 * wants to reword — everything else falls back to our catalog.
 *
 * Two kinds of override are skipped, in both directions, because a message can
 * be a string or a namespace and never both:
 *
 *  - replacing a whole namespace with one string — a tenant typo that would turn
 *    every `t('poi.something')` under it into a missing-message error;
 *  - replacing a string with a namespace. A tenant authoring `legend.title` for
 *    their legend panel would otherwise take out the app's own `legend` label,
 *    and `t('legend')` would resolve to an object. Their key is still reachable
 *    under `PROJECT_NAMESPACE`, which is where the app looks for tenant-named
 *    things.
 */
export function deepMerge(base: MessageTree, overrides: MessageTree): MessageTree {
  const merged: MessageTree = { ...base };

  for (const [key, value] of Object.entries(overrides)) {
    const existing = merged[key];
    if (existing !== undefined && typeof existing !== typeof value) continue;

    merged[key] =
      typeof existing === "object" && typeof value === "object"
        ? deepMerge(existing, value)
        : value;
  }

  return merged;
}

/**
 * The catalog a tenant's pages read: the repo `base` with the tenant's own
 * dictionary merged on top, and mounted whole under `PROJECT_NAMESPACE` after the
 * merge so it always wins there — that namespace is the app's, and a tenant key
 * literally called `project` is reachable inside it.
 */
export function tenantCatalog(base: MessageTree, tenant: MessageTree): MessageTree {
  return { ...deepMerge(base, tenant), [PROJECT_NAMESPACE]: tenant };
}

/** Widen a merged tree to what next-intl's config expects. */
export function asIntlMessages(tree: MessageTree): AbstractIntlMessages {
  return tree as AbstractIntlMessages;
}
